/**
 * LineItemTable — T-M8-005
 *
 * Displays the paginated list of line items for a project.
 * Features:
 *   - Tabular display of input dimensions + key derived columns
 *   - Inline edit via the EditLineItemModal (sheet/dialog)
 *   - Soft-delete with confirmation chip pattern (no dialog)
 *   - Load-more cursor pagination (hasMore → "Load more" button)
 *   - Empty state with CTA pointing to the calculator form
 *   - Skeleton loading state
 */
import * as React from 'react';
import {
  Pencil,
  Trash2,
  Loader2,
  ChevronDown,
  Layers,
} from 'lucide-react';
import type {
  LineItemDto,
  MaterialCode,
  CalcModeCode,
} from '@cutlyft/zod-schemas';
import { UpdateLineItemSchema, MATERIAL_CODES } from '@cutlyft/zod-schemas';
import { useLineItems } from '@/hooks/line-items/use-line-items';
import { useUpdateLineItem } from '@/hooks/line-items/use-update-line-item';
import { useDeleteLineItem } from '@/hooks/line-items/use-delete-line-item';
import { useCatalogueSeries } from '@/hooks/catalogue/use-catalogue-series';
import { useCatalogueTypes } from '@/hooks/catalogue/use-catalogue-types';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DynamicLineItemForm } from '@/components/DynamicLineItemForm';

/* ─── Props ─────────────────────────────────────────────────────────── */

interface LineItemTableProps {
  projectId: number;
}

/* ─── Material badge colour mapping ─────────────────────────────────── */

const MATERIAL_BADGE_VARIANT: Record<
  MaterialCode,
  'default' | 'secondary' | 'outline'
> = {
  ALUMINIUM: 'secondary',
  UPVC: 'default',
  ALUK: 'outline',
};

/* ─── Inline edit modal ─────────────────────────────────────────────── */

interface EditLineItemModalProps {
  item: LineItemDto;
  projectId: number;
  onClose: () => void;
}

type EditFormState = {
  widthMm: string;
  heightMm: string;
  qty: string;
  locationNotes: string;
  remarks: string;
  materialFilter: MaterialCode | 'ALL';
  seriesId: string;
  typeId: string;
  material: MaterialCode | '';
  mode: CalcModeCode | '';
};

function EditLineItemModal({ item, projectId, onClose }: EditLineItemModalProps) {
  const { toast } = useToast();
  const updateMutation = useUpdateLineItem();

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[900px] w-full max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Line Item #{item.id}</DialogTitle>
          <DialogDescription>
            Update dimensions or catalogue selection. The calculator re-runs automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {item.combinationId ? (
            <DynamicLineItemForm
              combinationId={item.combinationId}
              materialCode={item.material}
              initialValues={{
                ...item,
                inputUnit: item.inputUnit || 'mm',
                g1Value: item.g1Value || '',
                g2Value: item.g2Value || '',
              }}
              onSubmit={(values) => {
                const payload = {
                  ...values,
                  // keep original material etc. if not handled by dynamic form
                };

                const parsed = UpdateLineItemSchema.safeParse(payload);
                if (!parsed.success) {
                  toast({
                    variant: 'destructive',
                    title: 'Invalid input',
                    description: parsed.error.issues[0]?.message ?? 'Please check your values.',
                  });
                  return;
                }

                updateMutation.mutate(
                  { id: item.id, input: parsed.data },
                  {
                    onSuccess: () => {
                      toast({ title: 'Line item updated' });
                      onClose();
                    },
                    onError: (err: unknown) => {
                      const msg =
                        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data
                          ?.error?.message ?? '';
                      toast({
                        variant: 'destructive',
                        title: 'Failed to update line item',
                        description: msg || 'An unexpected error occurred.',
                      });
                    },
                  }
                );
              }}
              onReset={onClose}
              isSubmitting={updateMutation.isPending}
              submitLabel="Save Changes"
              isEdit
            />
          ) : (
            <div className="text-sm text-destructive p-4 border rounded-md">
              Legacy line items cannot be edited. Please delete and recreate.
            </div>
          )}
        </div>

      </DialogContent>
    </Dialog>
  );
}

/* ─── Row component ─────────────────────────────────────────────────── */

interface LineItemRowProps {
  item: LineItemDto;
  projectId: number;
  seriesMap: Map<number, string>;
}

const MODE_LABEL: Record<string, string> = {
  SLIDING: 'Sliding',
  CASEMENT: 'Casement',
};

function LineItemRow({ item, projectId, seriesMap }: LineItemRowProps) {
  const { toast } = useToast();
  const [editOpen, setEditOpen] = React.useState(false);
  const [confirmDelete, setConfirmDelete] = React.useState(false);

  const deleteMutation = useDeleteLineItem();
  const { data: typesData } = useCatalogueTypes(item.seriesId);

  const typeLabel = React.useMemo(() => {
    if (!item.typeId || !typesData) return '—';
    return typesData.find((t) => t.id === item.typeId)?.label ?? '—';
  }, [item.typeId, typesData]);

  const handleDelete = () => {
    deleteMutation.mutate(
      { id: item.id, projectId },
      {
        onSuccess: () => toast({ title: 'Line item deleted' }),
        onError: () =>
          toast({ variant: 'destructive', title: 'Failed to delete line item' }),
      },
    );
  };

  return (
    <>
      <tr
        className={[
          'border-b border-border text-sm transition-colors hover:bg-muted/30',
          item.isDeleted === 1 ? 'opacity-50' : '',
        ].join(' ')}
      >
        <td className="py-2 px-2 text-fg-muted font-mono text-xs w-9 shrink-0">{item.id}</td>
        <td className="py-2 px-2">
          <Badge
            variant={MATERIAL_BADGE_VARIANT[item.material]}
            className="text-[10px] font-medium whitespace-nowrap"
          >
            {item.material}
          </Badge>
        </td>
        <td className="py-2 px-2 text-fg-muted text-xs max-w-[100px] truncate">
          {item.locationNotes ?? '—'}
        </td>
        <td className="py-2 px-2 font-mono text-fg-primary tabular-nums text-xs whitespace-nowrap">
          {item.widthMm}×{item.heightMm}mm
        </td>
        <td className="py-2 px-2 text-center text-fg-primary font-medium text-xs">{item.qty}</td>
        <td className="py-2 px-2 text-fg-secondary text-xs whitespace-nowrap">
          {item.mode ? (MODE_LABEL[item.mode] ?? item.mode) : '—'}
        </td>
        <td className="py-2 px-2 text-fg-secondary text-xs max-w-[100px] truncate">
          {seriesMap.get(item.seriesId) ?? '—'}
        </td>
        <td className="py-2 px-2 text-fg-secondary text-xs max-w-[100px] truncate">
          {typeLabel}
        </td>
        <td className="py-1.5 px-2 sticky right-0 bg-background border-l border-border/50">
          {item.isDeleted === 0 ? (
            <div className="flex items-center justify-end gap-0.5">
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0"
                onClick={() => setEditOpen(true)}
                title="Edit line item"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              {confirmDelete ? (
                <>
                  <Button
                    size="sm"
                    variant="destructive"
                    className="h-7 text-xs px-2"
                    onClick={handleDelete}
                    disabled={deleteMutation.isPending}
                  >
                    {deleteMutation.isPending ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      'OK'
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs px-1.5"
                    onClick={() => setConfirmDelete(false)}
                  >
                    ✕
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                  onClick={() => setConfirmDelete(true)}
                  title="Delete line item"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          ) : (
            <Badge variant="outline" className="text-[10px]">
              Deleted
            </Badge>
          )}
        </td>
      </tr>

      {editOpen && (
        <EditLineItemModal
          item={item}
          projectId={projectId}
          onClose={() => setEditOpen(false)}
        />
      )}
    </>
  );
}

/* ─── Skeleton rows ─────────────────────────────────────────────────── */

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 3 }).map((_, i) => (
        <tr key={i} className="border-b border-border">
          {Array.from({ length: 9 }).map((_, j) => (
            <td key={j} className="py-3 px-2">
              <Skeleton className="h-3 w-full rounded" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

/* ─── Main export ─────────────────────────────────────────────────────── */

/**
 * Renders a table of line items for the given project, with inline edit and delete.
 * Cursor-paginated via "Load more" button when `hasMore` is true.
 */
export function LineItemTable({ projectId }: LineItemTableProps) {
  const [cursor, setCursor] = React.useState<number | undefined>(undefined);
  const [allItems, setAllItems] = React.useState<LineItemDto[]>([]);

  const { data: allSeries } = useCatalogueSeries();
  const seriesMap = React.useMemo<Map<number, string>>(() => {
    const map = new Map<number, string>();
    allSeries?.forEach((s) => map.set(s.id, s.label));
    return map;
  }, [allSeries]);

  const { data, isLoading, isFetching } = useLineItems(projectId, {
    limit: 50,
    cursor,
    status: 'active',
  });

  // Append new page results to local state (simulated infinite scroll via button)
  React.useEffect(() => {
    if (!data) return;
    if (cursor == null) {
      // Initial / refresh load — replace
      setAllItems(data.items);
    } else {
      // Load-more — append
      setAllItems((prev) => {
        const existingIds = new Set(prev.map((i) => i.id));
        const newItems = data.items.filter((i) => !existingIds.has(i.id));
        return [...prev, ...newItems];
      });
    }
  }, [data]);

  // Reset pagination when projectId changes
  React.useEffect(() => {
    setCursor(undefined);
    setAllItems([]);
  }, [projectId]);

  const handleLoadMore = () => {
    if (data?.nextCursor) {
      setCursor(data.nextCursor);
    }
  };

  // When the component remounts (e.g. switching tabs), `allItems` resets to []
  // but TanStack Query immediately returns the cached data with isLoading=false.
  // The useEffect above populates allItems on the next tick, causing a one-frame
  // flash of the empty state. Fall back to query data directly to avoid this.
  const displayItems = allItems.length > 0 ? allItems : (data?.items ?? []);

  /* Loading state — initial fetch with no data at all */
  if (isLoading && displayItems.length === 0) {
    return (
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-sm">
          <TableHead />
          <tbody>
            <SkeletonRows />
          </tbody>
        </table>
      </div>
    );
  }

  /* Empty state — only when the query is settled and there are genuinely no items */
  if (!isLoading && displayItems.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-12 text-center">
        <Layers className="mb-3 h-8 w-8 text-fg-muted" />
        <p className="text-sm font-medium text-fg-primary">No line items yet</p>
        <p className="mt-1 text-xs text-fg-muted">
          Use the calculator above to add your first window or door.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-sm">
          <TableHead />
          <tbody>
            {displayItems.map((item) => (
              <LineItemRow key={item.id} item={item} projectId={projectId} seriesMap={seriesMap} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Load more */}
      {data?.hasMore && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={handleLoadMore}
            disabled={isFetching}
          >
            {isFetching ? (
              <>
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                Loading…
              </>
            ) : (
              <>
                <ChevronDown className="mr-2 h-3.5 w-3.5" />
                Load more
              </>
            )}
          </Button>
        </div>
      )}

      {/* Item count summary */}
      <p className="text-right text-xs text-fg-muted">
        {displayItems.length} {displayItems.length === 1 ? 'item' : 'items'} shown
        {data?.hasMore ? ' · more available' : ''}
      </p>
    </div>
  );
}

/* ─── Table header ──────────────────────────────────────────────────── */

function TableHead() {
  const th = 'py-2 px-2 text-left text-[10px] font-semibold uppercase tracking-wider text-fg-muted';
  return (
    <thead>
      <tr className="border-b border-border bg-muted/40">
        <th className={`${th} w-9`}>#</th>
        <th className={th}>Material</th>
        <th className={th}>Location</th>
        <th className={th}>W × H</th>
        <th className={`${th} text-center`}>Qty</th>
        <th className={th}>Window Type</th>
        <th className={th}>Series</th>
        <th className={th}>Type</th>
        <th className="py-2 px-2 text-right text-[10px] font-semibold uppercase tracking-wider text-fg-muted sticky right-0 bg-muted/40 border-l border-border/50">
          Actions
        </th>
      </tr>
    </thead>
  );
}

// ⚠️ FILE NEARING LIMIT — PLAN SPLIT if component grows further
